import { IssueCaseCategory, IssueCaseStatusCode, IssueTaskCategory, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { Card, Col, Row, Statistic, Typography } from 'antd';
import { FC } from 'react';
import {
  ISSUE_CASE_ACTIVE_STATUSES,
  ISSUE_CASE_ALL_CATEGORIES,
  ISSUE_TASK_ACTIVE_STATUSES,
  ISSUE_TASK_ALL_CATEGORIES,
  issueTaskCategoryKr,
} from 'src/constants/issue.constants';

import { categoryColorMap } from 'src/utils/issue/issue-ag-grid-util';
import { getCategoryLabel } from 'src/utils/issue/issue-data-util';
import {
  issueCaseStatusBadgeMap,
  issueStatusColorMap,
  issueTaskCategoryColorMap,
  issueTaskStatusBadgeMap,
} from 'src/utils/issue/issue-util';

import IssueRankingList from 'src/components/Issue/common/IssueRankingList';

import { IssueStoreSummary } from 'src/hooks/subscription/useIssueStoreManagementData';

const { Text } = Typography;

interface IssueStoreSummaryCardProps {
  summary: IssueStoreSummary;
  isLoading: boolean;
  storeName?: string;
}

const IssueStoreSummaryCard: FC<IssueStoreSummaryCardProps> = ({ summary, isLoading, storeName }) => {
  // 모든 상태/분류에 대해 count가 없으면 0으로 표시
  const caseStatusEntries = ISSUE_CASE_ACTIVE_STATUSES.map(
    (status) => [status, summary.byStatus[status] || 0] as [IssueCaseStatusCode, number]
  );
  const caseCategoryEntries = ISSUE_CASE_ALL_CATEGORIES.map(
    (category) => [category, summary.byCategory[category] || 0] as [IssueCaseCategory, number]
  );
  const taskStatusEntries = ISSUE_TASK_ACTIVE_STATUSES.map(
    (status) => [status, summary.taskByStatus[status] || 0] as [IssueTaskStatus, number]
  );
  const taskCategoryEntries = ISSUE_TASK_ALL_CATEGORIES.map(
    (category) => [category, summary.taskByCategory[category] || 0] as [IssueTaskCategory, number]
  );

  const title = storeName ? `${storeName} 이슈 요약` : '매장 이슈 요약';

  return (
    <Card size='small' title={title} loading={isLoading}>
      <Row gutter={24}>
        {/* 좌측: 케이스/태스크 요약 */}
        <Col span={18}>
          {/* 케이스 요약 */}
          <Row gutter={[24, 16]}>
            <Col span={3}>
              <Statistic
                title='케이스'
                value={summary.total}
                suffix='건'
                valueStyle={{ fontSize: 20, fontWeight: 600 }}
              />
            </Col>

            <Col span={10}>
              <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
                케이스 상태별
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                {caseStatusEntries.map(([status, count]) => {
                  const badge = issueCaseStatusBadgeMap[status];
                  const color = badge ? issueStatusColorMap[badge.status] : '#8c8c8c';
                  return (
                    <div key={status} style={{ textAlign: 'center', minWidth: 40 }}>
                      <div style={{ color, fontSize: 16, fontWeight: 600 }}>{count}</div>
                      <div style={{ fontSize: 10, color: '#666' }}>{badge?.text || status}</div>
                    </div>
                  );
                })}
              </div>
            </Col>

            <Col span={11}>
              <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
                케이스 분류별
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                {caseCategoryEntries.map(([category, count]) => {
                  const color = categoryColorMap[category] || '#8c8c8c';
                  const label = getCategoryLabel(category);
                  return (
                    <div key={category} style={{ textAlign: 'center', minWidth: 40 }}>
                      <div style={{ color, fontSize: 16, fontWeight: 600 }}>{count}</div>
                      <div style={{ fontSize: 10, color }}>{label}</div>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>

          {/* 태스크 요약 */}
          <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
            <Col span={3}>
              <Statistic
                title='태스크'
                value={summary.taskTotal}
                suffix='건'
                valueStyle={{ fontSize: 20, fontWeight: 600 }}
              />
            </Col>

            <Col span={10}>
              <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
                태스크 상태별
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                {taskStatusEntries.map(([status, count]) => {
                  const badge = issueTaskStatusBadgeMap[status];
                  const color = badge ? issueStatusColorMap[badge.status] : '#8c8c8c';
                  return (
                    <div key={status} style={{ textAlign: 'center', minWidth: 40 }}>
                      <div style={{ color, fontSize: 16, fontWeight: 600 }}>{count}</div>
                      <div style={{ fontSize: 10, color: '#666' }}>{badge?.text || status}</div>
                    </div>
                  );
                })}
              </div>
            </Col>

            <Col span={11}>
              <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
                태스크 분류별
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                {taskCategoryEntries.map(([category, count]) => {
                  const color = issueTaskCategoryColorMap[category] || '#8c8c8c';
                  const label = issueTaskCategoryKr[category] || category;
                  return (
                    <div key={category} style={{ textAlign: 'center', minWidth: 40 }}>
                      <div style={{ color, fontSize: 16, fontWeight: 600 }}>{count}</div>
                      <div style={{ fontSize: 10, color }}>{label}</div>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Col>

        {/* 우측: 이슈 상품 Top 5 */}
        <Col span={6}>
          <IssueRankingList title='이슈 상품 Top 5' items={summary.topProducts} emptyText='상품 없음' />
        </Col>
      </Row>
    </Card>
  );
};

export default IssueStoreSummaryCard;
